/**
 * Dice engine — Domain layer, pure and testable.
 *
 * The mixed sheet is the dice-first half of the project: ability scores can be
 * rolled (4d6, drop the lowest, six times), every check is a d20 with a
 * modifier, and advantage/disadvantage is two d20s keeping the higher/lower.
 * This module is the single place where that arithmetic lives, so the builder,
 * the sheet and the smoke tests all agree.
 *
 * Notation supported: `d20`, `4d6`, `4d6dl1` (drop lowest), `2d20kh1` (keep
 * highest), `2d20kl1` (keep lowest), each optionally with `+ N` / `- N`.
 * Everything else parses to null rather than guessing.
 *
 * Randomness is injectable so a roll can be replayed in a test.
 */

export type Random = () => number;

export interface DieRoll {
  sides: number;
  value: number;
  /** false when a keep/drop rule discarded this die. */
  kept: boolean;
  /** Why it was discarded — shown to the player, never hidden. */
  discarded?: 'dropped-lowest' | 'dropped-highest' | 'kept-lowest' | 'kept-highest';
}

export type Advantage = 'advantage' | 'disadvantage';

export interface RollResult {
  /** The notation as given, echoed back for the log. */
  notation: string;
  dice: DieRoll[];
  modifier: number;
  total: number;
  /** Natural dice total, before the modifier — what "a nat 20" refers to. */
  diceTotal: number;
  advantage: Advantage | null;
  /** Critical hit / critical miss on a single d20 (natural 20 / natural 1). */
  critical: 'hit' | 'miss' | null;
  label: string | null;
}

export interface ParsedNotation {
  count: number;
  sides: number;
  modifier: number;
  /** `dl1` → drop the lowest, `kh1` → keep the highest, `kl1` → keep the lowest. */
  keep: { mode: 'drop-lowest' | 'drop-highest' | 'keep-highest' | 'keep-lowest'; count: number } | null;
}

const NOTATION = /^(\d*)d(\d+)(?:(dl|dh|kh|kl)(\d+))?([+-]\d+)?$/i;

export function parseNotation(text: string): ParsedNotation | null {
  const match = NOTATION.exec(text.trim().replace(/\s+/g, ''));
  if (!match) return null;
  const count = match[1] === '' || match[1] === undefined ? 1 : Number(match[1]);
  const sides = Number(match[2]);
  if (!Number.isFinite(count) || !Number.isFinite(sides) || count < 1 || sides < 2 || count > 100) {
    return null;
  }
  const mode = match[3]?.toLowerCase();
  const keepCount = match[4] === undefined ? 1 : Number(match[4]);
  const keep =
    mode === 'dl'
      ? ({ mode: 'drop-lowest', count: keepCount } as const)
      : mode === 'dh'
        ? ({ mode: 'drop-highest', count: keepCount } as const)
        : mode === 'kh'
          ? ({ mode: 'keep-highest', count: keepCount } as const)
          : mode === 'kl'
            ? ({ mode: 'keep-lowest', count: keepCount } as const)
            : null;
  return { count, sides, modifier: match[5] === undefined ? 0 : Number(match[5]), keep };
}

/** One die: uniform 1..sides, using the injected randomness. */
export function rollDie(sides: number, random: Random = Math.random): number {
  return 1 + Math.floor(random() * sides);
}

/**
 * Roll a notation string. Returns null for anything unparseable — callers
 * decide whether that is an error or simply "no dice".
 */
export function rollNotation(
  notation: string,
  random: Random = Math.random,
  options: { label?: string; advantage?: Advantage | null } = {},
): RollResult | null {
  const parsed = parseNotation(notation);
  if (!parsed) return null;

  const rolls = Array.from({ length: parsed.count }, () =>
    rollDie(parsed.sides, random),
  ).map<DieRoll>((value) => ({ sides: parsed.sides, value, kept: true }));

  if (parsed.keep && parsed.keep.count > 0 && parsed.count > 1) {
    const asc = [...rolls].sort((a, b) => a.value - b.value);
    const descending = [...asc].reverse();
    const discard =
      parsed.keep.mode === 'drop-lowest'
        ? asc.slice(0, parsed.keep.count)
        : parsed.keep.mode === 'drop-highest'
          ? descending.slice(0, parsed.keep.count)
          : parsed.keep.mode === 'keep-highest'
            ? asc.slice(0, Math.max(0, parsed.count - parsed.keep.count))
            : descending.slice(0, Math.max(0, parsed.count - parsed.keep.count));
    const reason: NonNullable<DieRoll['discarded']> =
      parsed.keep.mode === 'drop-lowest'
        ? 'dropped-lowest'
        : parsed.keep.mode === 'drop-highest'
          ? 'dropped-highest'
          : parsed.keep.mode === 'keep-highest'
            ? 'kept-highest'
            : 'kept-lowest';
    for (const die of discard) {
      die.kept = false;
      die.discarded = reason;
    }
  }

  const diceTotal = rolls.filter((die) => die.kept).reduce((sum, die) => sum + die.value, 0);
  const advantage = options.advantage ?? null;
  const single = parsed.count === 1 && parsed.sides === 20;
  const natural = rolls[0]?.value ?? 0;

  return {
    notation,
    dice: rolls,
    modifier: parsed.modifier,
    diceTotal,
    total: diceTotal + parsed.modifier,
    advantage,
    critical: single && advantage === null ? (natural === 20 ? 'hit' : natural === 1 ? 'miss' : null) : null,
    label: options.label ?? null,
  };
}

/** A d20 test: 2d20 keep highest for advantage, keep lowest for disadvantage. */
export function rollD20(
  modifier: number,
  options: { advantage?: Advantage | null; label?: string; random?: Random } = {},
): RollResult {
  const advantage = options.advantage ?? null;
  const random = options.random ?? Math.random;
  if (advantage === null) {
    const result = rollNotation(`d20${modifier >= 0 ? '+' : ''}${modifier}`, random, {
      label: options.label,
    });
    return result as RollResult;
  }
  const keep = advantage === 'advantage' ? 'kh1' : 'kl1';
  const result = rollNotation(`2d20${keep}${modifier >= 0 ? '+' : ''}${modifier}`, random, {
    label: options.label,
    advantage,
  });
  return result as RollResult;
}

/* ------------------------------------------------------------- ability scores */

export const ABILITY_SCORE_DICE = '4d6dl1';

/** One ability score the classic way: 4d6, drop the lowest. */
export function rollAbilityScore(random: Random = Math.random): RollResult {
  return rollNotation(ABILITY_SCORE_DICE, random) as RollResult;
}

/** Six ability scores, in order — the player assigns them to abilities. */
export function rollAbilityScores(random: Random = Math.random): RollResult[] {
  return Array.from({ length: 6 }, () => rollAbilityScore(random));
}

/** Advantage/disadvantage cancel out — one of each is a straight roll. */
export function cancelAdvantage(
  sources: Advantage[],
): Advantage | null {
  const advantage = sources.filter((source) => source === 'advantage').length;
  const disadvantage = sources.filter((source) => source === 'disadvantage').length;
  if (advantage === disadvantage) return null;
  return advantage > disadvantage ? 'advantage' : 'disadvantage';
}

/** Human-readable line for a roll log. Turkish dice names stay 'd20' etc. */
export function formatRoll(result: RollResult, locale: 'tr' | 'en' = 'en'): string {
  const mod = result.modifier === 0 ? '' : result.modifier > 0 ? ` + ${result.modifier}` : ` − ${Math.abs(result.modifier)}`;
  const dice = result.dice
    .map((die) => (die.kept ? `${die.value}` : `(${die.value})`))
    .join(', ');
  const prefix = result.label ? `${result.label}: ` : '';
  const adv =
    result.advantage === 'advantage'
      ? locale === 'tr'
        ? ' avantaj'
        : ' with advantage'
      : result.advantage === 'disadvantage'
        ? locale === 'tr'
          ? ' dezavantaj'
          : ' with disadvantage'
        : '';
  const crit =
    result.critical === 'hit'
      ? locale === 'tr'
        ? ' · kritik!'
        : ' · critical!'
      : result.critical === 'miss'
        ? locale === 'tr'
          ? ' · kritik ıskalama'
          : ' · critical miss'
        : '';
  return `${prefix}${result.notation}${adv} → [${dice}]${mod} = ${result.total}${crit}`;
}
